import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row, Nav, Tab, Card } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";

import "react-international-phone/style.css";

import Files from "../../files";
import ConditionalButton from "../../../layout/conditional-button";
import ConditionalDropdown from "../../../layout/conditional-dropdown";
import Calendar from "../../../layout/calendar";
import ErrorModal from "../../../layout/modal";
import ConfirmModal from "../../../layout/modal";
import EmailComposeModal from "../../../layout/email-compose-modal";
import InviteModal from "./modals/invite";
import ProcessModal from "./modals/process";
import ReportModal from "./modals/report";
import ExtractModal from "./modals/extract";
import DeletePersonModal from "./modals/delete";
import ClockModal from "./modals/clock";
import ExpenseModal from "./modals/expense";
import PaymentModal from "./modals/payment";
import BillingModal from "./modals/invoice";
import LeadActivityModal from "./modals/lead-activity";
import LeadConversionModal from "./modals/lead-conversion";

import { isGuid, sanitizeFilename } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";
import {
    style as renderingStyle,
    fileName as renderingFileName,
    download as renderedDownload,
    generators as renderingGenerators,
} from "../../../utils/rendering";
import { applyCustomValues, getTemplateCustomFields } from "../../../utils/template-custom-fields";
import { firmSignatureRecipient, templateFirmSettings } from "../../../utils/firm";

import { send as pageInfo, loading } from "../../../actions/page";
import { get as getPerson, save as savePerson, destroy as deletePerson, archive as archivePerson, completeExtract } from "../../../actions/people";
import { get as getCompany, save as saveCompany } from "../../../actions/companies";
import { get as getJudge, save as saveJudge, destroy as deleteJudge } from "../../../actions/judges";
import { get as getLead, save as saveLead, destroy as deleteLead, activities as getLeadActivities, saveActivity as saveLeadActivity, completeActivity as completeLeadActivity, deleteActivity as deleteLeadActivity, convert as convertLead } from "../../../actions/leads";
import { get as getStaff, save as saveStaff, destroy as deleteStaff } from "../../../actions/staff";
import { get as getClient, save as saveClient, destroy as deleteClient } from "../../../actions/clients";
import { get as getAttorney, save as saveAttorney, destroy as deleteAttorney } from "../../../actions/attorneys";
import { get as getVendor, save as saveVendor, destroy as deleteVendor } from "../../../actions/vendors";
import { get as getClocks } from "../../../actions/clocks";
import { list as getRates } from "../../../actions/rates";
import { flagged as getReports, render, inline, get as getReport, prepareSignature } from "../../../actions/report.template";
import { flagged as getEmails, send as sendEmail } from "../../../actions/email.template";
import { get as getLogs, save as saveLog } from "../../../actions/logs";
import { list as getSurveys, invites as getInvites, invite, uninvite, remind } from "../../../actions/surveys";
import { get as getSettings } from "../../../actions/settings";
import { generate as generateInvoice, destroy as deleteActivity, get as getInvoice } from "../../../actions/invoices";
import { get as getMatters } from "../../../actions/matters";
import { send as sendAlert } from "../../../actions/alert";

import Personal from "./personal";
import Judge from "./judge";
import Attorney from "../attorney";
import Lead from "./lead";
import Client from "./client";
import Staff from "./staff";
import Vendor from "./vendor";
import CompanyPeople from "./company-people";
import Board from "./board";
import Matters from "./matters";
import SignatureRequests from "./signature-requests";
import Activity from "./activity";
import TrustActivity from "./trust-activity";
import Notes from "../../../layout/logs";
import NavigationGuard from "../../../layout/navigation-guard";
import ScrollToTopButton from "../../settings/template/scroll-to-top-button";

import NavItem from "../../../layout/tabs/item";
import { FEATURES, has as hasFeature } from "../../../constants/features";
import {
    DELETE_ATTORNEY, DELETE_CLIENT, DELETE_JUDGE, DELETE_LEAD, DELETE_STAFF, DELETE_VENDOR, EDIT_ATTORNEY, EDIT_CLIENT, EDIT_CLOCK, EDIT_EXPENSE, EDIT_INVOICE, EDIT_JUDGE, EDIT_LEAD, EDIT_MATTER, EDIT_PAYMENT, EDIT_STAFF, EDIT_VENDOR
 } from "../../../constants/permissions";


export const TABS = {
    JUDGE: 1,
    LEAD: 2,
    CLIENT: 4,
    STAFF: 8,
    ATTORNEY: 16,
    VENDOR: 32
};

export const GENERATORS = {
    EMAIL: 1,
    REPORT: 2,
    EXTRACT: 3
};

const COMPLETE_EXTRACT_OPTION = {
    uuid: "complete-person-extract",
    caption: "Export",
    name: "COMPLETE_EXTRACT",
    category: "Extracts",
    style: GENERATORS.EXTRACT
};

function profileDisplayName(person = {}) {
    return (
        person.displayName ||
        person.name ||
        [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" ") ||
        person.email ||
        ""
    ).trim();
}

function profileSignatureRecipients(person = {}, firm = {}) {
    const email = String(person.email || "").trim();
    const recipients = [];

    if (email) {
        recipients.push({
            key: "client",
            name: profileDisplayName(person) || email,
            email,
            role: "client",
            personUuid: person.uuid,
        });
    }

    const firmRecipient = firmSignatureRecipient(firm);
    if (firmRecipient) recipients.push(firmRecipient);

    return recipients;
}

function signatureRequestTemplateData(enabled) {
    if (!enabled) return {};

    return {
        prepare: true,
        SignatureRequest: {
            prepare: true,
            action: "prepare",
            mode: "draft",
            source: "matters.firm",
        },
    };
}

function reportErrorMessage(err) {
    return err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to generate the selected report.";
}

const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#39;"
}[char]));

const hasText = (value) => String(value ?? "").trim().length > 0;

const emailField = (label, value, { always = false } = {}) => {
    if (!always && !hasText(value)) return "";
    return `<div style="margin:0 0 6px 0;"><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</div>`;
};

const formatEmailSender = ({ display, from }) => {
    if (hasText(display) && hasText(from)) return `${display} <${from}>`;
    return display || from || "";
};

const buildSentEmailNote = (email, sentAt = new Date()) => {
    const body = hasText(email?.body) ? email.body : "<p><em>No body.</em></p>";

    return [
        `<h3 style="margin:0 0 12px 0;">Email Sent</h3>`,
        `<div style="margin:0 0 16px 0;">`,
        emailField("Sent", sentAt.toLocaleString(), { always: true }),
        emailField("From", formatEmailSender(email), { always: true }),
        emailField("To", email?.to, { always: true }),
        emailField("CC", email?.cc),
        emailField("BCC", email?.bcc),
        emailField("Subject", email?.subject, { always: true }),
        emailField("Configuration", email?.configuration),
        `</div>`,
        `<div style="border-top:1px solid #dee2e6; padding-top:12px;">${body}</div>`
    ].join("");
};

class Detail extends React.Component {
    mattersRef = React.createRef();
    activityRef = React.createRef();
    billingRef = React.createRef();
    staffRef = React.createRef();
    attorneyRef = React.createRef();
    leadRef = React.createRef();
    clientRef = React.createRef();
    judgeRef = React.createRef();
    vendorRef = React.createRef();
    companyPeopleRef = React.createRef();
    notesRef = React.createRef();
    boardRef = React.createRef();

    constructor(props) {
        super(props);
        const params = new URLSearchParams(window.location.search || "");
        this.props.loading(true);
        this.state = {
            selected: params.get("tab") === "board" ? "board" : "profile",
            person: { flags: 0, email: "", originalEmail: "" },
            attorney: {},
            judge: {},
            lead: {},
            client: {},
            staff: {},
            vendor: {},
            logs: [],
            surveys: [],
            invites: [],
            reports: [],
            modal: {
                error: {
                    show: false
                },
                confirm: {
                    show: false
                },
                invite: {
                    show: false
                },
                process: {
                    show: false
                },
                report: {
                    show: false,
                    working: false,
                    workingLabel: ""
                },
                extract: {
                    show: false,
                    report: null
                },
                delete: {
                    show: false
                },
                email: {
                    show: false
                },
                clock: {
                    show: false,
                    uuid: null
                },
                expense: {
                    show: false,
                    uuid: null
                },
                payment: {
                    show: false,
                    uuid: null
                },
                leadActivity: {
                    show: false,
                    uuid: null,
                    type: "NOTE",
                    subject: "",
                    body: "",
                    dueAt: null,
                    completedAt: null
                },
                leadConversion: {
                    show: false
                },
                billing: {
                    show: false
                }
            },
            changed: false,
            pendingNote: false,
            surveyMenu: {
                show: false,
                surveyUuid: null,
                target: null
            },
            uuid: props.params.uuid ?? uuidv4()
        };
        this.openBoardTaskUuid = params.get("task");
        this.defaultCountry = "us";
        this.loading = false;
        this.props.pageInfo({ title: this.entityName, breadcrumb: `[New ${this.entityName}]` });
    }

    notifySuccess = (message) => {
        this.props.sendAlert(message, "success");
    }

    queueFlash = (message, type = "success") => {
        try {
            sessionStorage.setItem("personEditorFlash", JSON.stringify({ message, type }));
        } catch {
            // Storage is best-effort; the mutation has already succeeded.
        }
    }

    flushQueuedFlash = () => {
        try {
            const raw = sessionStorage.getItem("personEditorFlash");
            if (!raw) return;
            sessionStorage.removeItem("personEditorFlash");
            const flash = JSON.parse(raw);
            if (flash?.message) this.props.sendAlert(flash.message, flash.type || "success");
        } catch {
            sessionStorage.removeItem("personEditorFlash");
        }
    }

    get features() {
        return Number(localStorage.getItem("features"));
    }

    get isNew() {
        return !isGuid(this.props.params.uuid);
    }

    get isCompany() {
        return Boolean(this.props.company);
    }

    get entityName() {
        return this.isCompany ? "Company" : "Person";
    }

    get entityPath() {
        return this.isCompany ? "/dashboard/companies/" : "/dashboard/people/";
    }

    get displayName() {
        const person = this.state.person || {};
        return person.displayName || [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
    }

    get calendarScopeLabel() {
        const person = this.state.person || {};
        const firstLast = [person.firstName, person.lastName].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
        return firstLast || this.displayName || "Person";
    }

    get leadConversionMatterCaption() {
        return this.state.lead?.subject || `Matter for ${this.displayName || "Client"}`;
    }

    get boardPersonType() {
        if (this.has({ flag: TABS.LEAD })) return "lead";
        if (this.has({ flag: TABS.CLIENT })) return "client";
        if (this.has({ flag: TABS.VENDOR })) return "vendor";
        if (this.has({ flag: TABS.ATTORNEY })) return "attorney";
        if (this.has({ flag: TABS.STAFF })) return "staff";
        if (this.has({ flag: TABS.JUDGE })) return "judge";
        return "person";
    }

    onSubmit = async (e) => {
        e.preventDefault();
        await this.onSaveClick(e);
    }

    onSurveyReview = async (invite) => {
        const matterResult = await this.props.getMatters({ personUuid: this.state.uuid, staffUuid: "X" });
        const matters = matterResult?.items || [];

        this.setState(prev => ({
            modal: {
                ...prev.modal,
                invite: {
                    ...prev.modal.invite,
                    show: false
                },
                process: {
                    ...prev.modal.process,
                    show: true,
                    title: invite.Survey.name,
                    data: invite.meta.data,
                    flags: invite.Survey.flags,
                    person: this.state?.person,
                    matters
                }
            }
        }));
    }

    onSurveyRemind = async (invite) => {
        await this.props.remind({
            uuid: invite.uuid,
            email: this.state.person.email,
            displayName: this.state.person.firstName + " " + this.state.person.lastName
        });
        this.notifySuccess("Survey reminder sent.");
    }

    onSurveyDelete = async (invite) => {
        const result = await this.props.uninvite({ uuid: invite.uuid });
        if (result) await this.loadInvites(true);
        this.onConfirmHideModal();
        this.notifySuccess("Survey invitation deleted.");
    }

    has = ({ flag, flags }) => Boolean((flags ?? this.state.person?.flags ?? 0) & flag);

    permitted = (named) => {
        const __permissions = (Array.isArray(named)) ? named : [named];
        return this.props.auth.permissions.some(item => __permissions.includes(item));
    }

    get shouldConvertLeadToClient() {
        const leadStatus = String(this.state.lead?.status || "OPEN").toUpperCase();
        return (
            this.permitted(EDIT_CLIENT) &&
            this.has({ flag: TABS.LEAD }) &&
            !this.has({ flag: TABS.CLIENT }) &&
            leadStatus !== "HIRED" &&
            !isGuid(this.state.lead?.convertedClientUuid) &&
            typeof this.props.convertLead === "function"
        );
    }

    loadInvites = async (updateState = false) => {
        const invites = await this.props.getInvites({ personUuid: this.state.uuid });
        if (updateState) {
            this.setState({
                invites: [...invites]
            });
        }
        return invites;
    }

    reportFlags(personFlags) {
        let result = 1;
        if (this.has({ flag: 1, flags: personFlags })) result += 16;
        if (this.has({ flag: 2, flags: personFlags })) result += 2;
        if (this.has({ flag: 4, flags: personFlags })) result += 4;
        if (this.has({ flag: 8, flags: personFlags })) result += 32;
        return result;
    }

    loadData = async () => {
        if (this.loading) return;
        this.loading = true;

        this.props.loading(true);

        if (!this.isNew) {
            const person = this.isCompany
                ? await this.props.getCompany({ uuid: this.state.uuid })
                : await this.props.getPerson({ uuid: this.state.uuid });
            const logs = await this.props.getLogs({ target: this.state.uuid });

            if (person) {
                let judge = {}, lead = {}, staff = {}, client = {}, attorney = {}, vendor = {};
                const surveys = await this.props.getSurveys({ flags: person.flags, hidden: false });
                const invites = await this.loadInvites(false);
                const reports = (await this.props.getReports({ flags: this.reportFlags(person.flags) }) || []).map((o) => ({
                    ...o,
                    style: GENERATORS.REPORT,
                }));
                const emails = (await this.props.getEmails({ flags: this.reportFlags(person.flags) }) || []).map((o) => ({
                    ...o,
                    style: GENERATORS.EMAIL,
                }));
                

                if (!this.isCompany && this.has({ flag: TABS.JUDGE, flags: person.flags }) && (this.permitted(EDIT_JUDGE))) {
                    judge = await this.props.getJudge({ uuid: this.state.uuid });
                }

                if (!this.isCompany && this.has({ flag: TABS.ATTORNEY, flags: person.flags }) && (this.permitted(EDIT_ATTORNEY))) {
                    attorney = await this.props.getAttorney({ uuid: this.state.uuid });
                }

                if (this.has({ flag: TABS.LEAD, flags: person.flags }) && (this.permitted(EDIT_LEAD))) {
                    lead = await this.props.getLead({ uuid: this.state.uuid });
                    if (!lead?.rates) {
                        lead.rates = await this.props.getRates();
                    }
                }

                if (this.has({ flag: TABS.CLIENT, flags: person.flags }) && (this.permitted(EDIT_CLIENT))) {
                    client = await this.props.getClient({ uuid: this.state.uuid });
                    if (!client?.rates) {
                        client.rates = await this.props.getRates();
                    }
                }

                if (this.has({ flag: TABS.STAFF, flags: person.flags }) && (this.permitted(EDIT_STAFF))) {
                    staff = await this.props.getStaff({ uuid: this.state.uuid });
                }

                if (this.has({ flag: TABS.VENDOR, flags: person.flags }) && (this.permitted(EDIT_VENDOR))) {
                    vendor = await this.props.getVendor({ uuid: this.state.uuid });
                }

                this.setState({
                    person: { ...person, originalEmail: person.email },
                    judge: { ...judge },
                    attorney: { ...attorney },
                    lead: { ...lead },
                    client: { ...client },
                    staff: { ...staff },
                    vendor: { ...vendor },
                    logs: [...logs.items],
                    surveys: [...surveys],
                    invites: [...invites],
                    reports: [...reports, ...emails, COMPLETE_EXTRACT_OPTION],
                    changed: false
                });
                await this.props.pageInfo({
                    title: `${this.entityName} (${person.displayName || [person.firstName, person.lastName].filter(Boolean).join(" ")})`,
                    breadcrumb: person.displayName || [person.firstName, person.lastName].filter(Boolean).join(" ")
                });
            }
        }
        this.props.loading(false);
        this.loading = false;
    }

    componentDidMount = async () => {
        this.mounted = true;
        await this.loadData();
        if (this.openBoardTaskUuid) {
            this.setState({ selected: "board" });
        }
        this.flushQueuedFlash();
        this.scrollToHashAnchor();
    }

    componentWillUnmount() { this.mounted = false; }

    componentDidUpdate(prevProps) {
        const prevUuid = prevProps.params?.uuid;
        const nextUuid = this.props.params?.uuid;

        if (prevUuid !== nextUuid) {
            this.setState({ uuid: nextUuid ?? uuidv4() }, async () => {
                await this.loadData();
                this.scrollToHashAnchor();
            });
            return;
        }

        if (prevProps.location?.hash !== this.props.location?.hash) {
            this.scrollToHashAnchor();
        }
    }

    scrollToHashAnchor = () => {
        const hash = this.props.location?.hash || window.location.hash;
        if (hash !== "#signature-requests") return;

        const scroll = () => {
            const element = document.getElementById("signature-requests");
            if (element) {
                element.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });
            }
        };

        if (this.state.selected !== "profile") {
            this.setState({ selected: "profile" }, () => window.setTimeout(scroll, 60));
            return;
        }

        window.setTimeout(scroll, 60);
    }

    doPostChange = () => {
        if (this.props.onChange) {
            this.props.onChange({ ...this.state });
        }
    }

    onContainedChange = (named, e) => {
        const { loading, ...clean } = (e || {});
        this.setState(
            prev => ({
                [named]: { ...prev[named], ...clean },
                changed: !this.loading
            }),
            this.doPostChange
        );
    };

    onPersonChange = (e) => {
        this.onContainedChange("person", e);
    }

    onSaveClick = async (e) => {
        this.props.loading(true);
        const person = this.state.person || {};
        const email = person.email;
        const originalEmail = person.originalEmail;

        if (email && email !== originalEmail) {
            const conflictingUser = await this.props.getPerson({ email });
            if (conflictingUser?.success) {
                this.onErrorShow({
                    title: "Person already exists",
                    body: "This email address is already in use with another person."
                });
                this.props.loading(false);
                return false;
            }
        }

        const result = this.isCompany
            ? await this.props.saveCompany(this.state.person)
            : await this.props.savePerson(this.state.person);

        if (!this.isCompany && this.permitted(EDIT_ATTORNEY) && this.has({ flag: TABS.ATTORNEY })) {
            const validation = this.attorneyRef.current?.validate?.();
            if (validation && !validation.success) {
                const firstError = Object.values(validation.errors || {})[0] || "Please correct the attorney profile.";
                this.onErrorShow({
                    title: "Attorney profile invalid",
                    body: firstError
                });
                this.props.loading(false);
                return false;
            }

            const attorneyValue = this.attorneyRef.current?.getValue?.() ?? this.state.attorney;
            await this.props.saveAttorney({ ...attorneyValue, uuid: result.uuid });
        }

        if (!this.isCompany && this.permitted(EDIT_JUDGE) && this.has({ flag: TABS.JUDGE })) {
            const validation = this.judgeRef.current?.validate?.();
            if (validation && !validation.success) {
                const firstError = Object.values(validation.errors || {})[0] || "Please correct the judge profile.";
                this.onErrorShow({
                    title: "Judge profile invalid",
                    body: firstError
                });
                this.props.loading(false);
                return false;
            }

            const judgeValue = this.judgeRef.current?.getValue?.() ?? this.state.judge;
            await this.props.saveJudge({ ...judgeValue, uuid: result.uuid });
        }

        if (this.permitted(EDIT_LEAD) && this.has({ flag: TABS.LEAD })) {
            const validation = this.leadRef.current?.validate?.();
            if (validation && !validation.success) {
                const firstError = Object.values(validation.errors || {})[0] || "Please correct the lead profile.";
                this.onErrorShow({
                    title: "Lead profile invalid",
                    body: firstError
                });
                this.props.loading(false);
                return false;
            }

            const leadValue = this.leadRef.current?.getValue?.() ?? this.state.lead;
            await this.props.saveLead({ ...leadValue, uuid: result.uuid });
        }

        if (this.permitted(EDIT_CLIENT) && this.has({ flag: TABS.CLIENT })) {
            const validation = this.clientRef.current?.validate?.();
            if (validation && !validation.success) {
                const firstError = Object.values(validation.errors || {})[0] || "Please correct the client profile.";
                this.onErrorShow({
                    title: "Client profile invalid",
                    body: firstError
                });
                this.props.loading(false);
                return false;
            }

            const clientValue = this.clientRef.current?.getValue?.() ?? this.state.client;
            await this.props.saveClient({ ...clientValue, uuid: result.uuid });
        }

        if (this.permitted(EDIT_STAFF) && this.has({ flag: TABS.STAFF })) {
            const validation = this.staffRef.current?.validate?.();
            if (validation && !validation.success) {
                const firstError = Object.values(validation.errors || {})[0] || "Please correct the staff profile.";
                this.onErrorShow({
                    title: "Staff profile invalid",
                    body: firstError
                });
                this.props.loading(false);
                return false;
            }

            const staffValue = this.staffRef.current?.getValue?.() ?? this.state.staff;
            await this.props.saveStaff({ ...staffValue, uuid: result.uuid });
        }

        if (this.permitted(EDIT_VENDOR) && this.has({ flag: TABS.VENDOR })) {
            const validation = this.vendorRef.current?.validate?.();
            if (validation && !validation.success) {
                const firstError = Object.values(validation.errors || {})[0] || "Please correct the vendor profile.";
                this.onErrorShow({
                    title: "Vendor profile invalid",
                    body: firstError
                });
                this.props.loading(false);
                return false;
            }

            const vendorValue = this.vendorRef.current?.getValue?.() ?? this.state.vendor;
            await this.props.saveVendor({ ...vendorValue, uuid: result.uuid });
        }

        if (this.isCompany && this.companyPeopleRef.current?.saveChanges) {
            await this.companyPeopleRef.current.saveChanges();
        }

        await this.savePendingNote(result.uuid);

        if (this.isNew) {
            this.suppressNavigationGuard = true;
            this.queueFlash(`${this.entityName} saved.`);
            this.props.navigate(this.entityPath + result.uuid, { replace: true });
            window.location.reload();
            return true;
        }

        await this.loadData();
        this.notifySuccess(`${this.entityName} saved.`);
        return true;
    };

    get hasPendingNote() {
        return Boolean(this.state.pendingNote || this.notesRef.current?.hasPendingText?.());
    }

    get hasUnsavedChanges() {
        return Boolean(this.state.changed || this.hasPendingNote || this.companyPeopleRef.current?.hasChanges?.());
    }

    onErrorShow = (config) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                error: {
                    ...config,
                    show: true
                }
            }
        }));
    }

    onErrorHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                error: {
                    ...prev.modal?.error,
                    show: false
                }
            }
        }));
    }

    onTabChange = (e, selected) => {
        e?.preventDefault?.();
        this.setState({
            selected
        });
    }

    onConfirmShow = (config) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                confirm: {
                    ...config,
                    show: true
                }
            }
        }));
    }

    onConfirmHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onConfirmCreateStaff = (e) => {
        this.setState(prev => ({
            selected: "staff",
            changed: true,
            person: {
                ...prev.person,
                flags: prev.person.flags | TABS.STAFF
            },
            staff: {},
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onConfirmCreateClient = (e) => {
        this.setState(prev => ({
            selected: "client",
            changed: true,
            person: {
                ...prev.person,
                flags: prev.person.flags | TABS.CLIENT
            },
            client: {
                ...prev.client,
                rates: prev.lead.rates,
                retainer: prev.lead.retainer,
                minutes: prev.lead.minutes
            },
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onConfirmCreateLead = async (e) => {
        const rates = await this.props.getRates();
        const creatorUuid = isGuid(this.props.auth?.user?.uuid)
            ? this.props.auth.user.uuid
            : isGuid(this.props.auth?.user?.Person?.uuid)
                ? this.props.auth.user.Person.uuid
                : null;

        this.setState(prev => ({
            selected: "lead",
            changed: true,
            lead: {
                ...prev.lead,
                assignedToUuid: prev.lead?.assignedToUuid || creatorUuid,
                rates
            },
            person: {
                ...prev.person,
                flags: prev.person.flags | TABS.LEAD
            },
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onClickDestroyLead = () => {
        this.onConfirmShow({
            title: "Remove lead profile?",
            body: "Click ok to remove the lead profile for this person.",
            onOk: this.onConfirmDestroyLead
        });
    };

    onConfirmDestroyLead = async () => {
        if (isGuid(this.state.lead?.uuid)) await this.props.deleteLead({ uuid: this.state.lead.uuid });
        this.notifySuccess("Lead profile removed.");

        this.setState(prev => ({
            selected: "profile",
            changed: true,
            person: {
                ...(prev.person || {}),
                flags: ((prev.person?.flags ?? 0) & ~TABS.LEAD)
            },
            lead: {},
            modal: {
                ...prev.modal,
                confirm: { ...prev.modal.confirm, show: false }
            }
        }));
    };

    onClickConvertLead = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                leadConversion: {
                    show: true
                }
            }
        }));
    };

    onLeadConversionHideModal = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                leadConversion: {
                    show: false
                }
            }
        }));
    };

    onConfirmConvertLead = async (payload = {}) => {
        this.onLeadConversionHideModal();
        this.props.loading(true);

        if (this.hasUnsavedChanges) {
            const saved = await this.onSaveClick();
            if (!saved) {
                this.props.loading(false);
                return;
            }
        }

        const result = await this.props.convertLead({ uuid: this.state.uuid, ...payload });
        if (result?.success) {
            await this.loadData();
            this.setState({ selected: "client" });
            this.notifySuccess(result.matterUuid ? "Lead converted to client and matter created." : "Lead converted to client.");
        } else {
            this.onErrorShow({
                title: "Conversion failed",
                body: "The lead could not be converted."
            });
        }

        this.props.loading(false);
    };

    onClickDestroyClient = () => {
        this.onConfirmShow({
            title: "Remove client profile?",
            body: "Click ok to remove the client profile for this person.",
            onOk: this.onConfirmDestroyClient
        });
    };

    onConfirmDestroyClient = async () => {
        if (isGuid(this.state.client?.uuid)) {
            const result = await this.props.deleteClient({ uuid: this.state.client.uuid });
            if (!result?.success) {
                this.onConfirmHideModal();
                this.onErrorShow({
                    title: "Client profile not removed",
                    body: result?.error?.message || "The client profile could not be removed. If this client has matters, invoices, payments, or trust activity, those records must remain attached."
                });
                return;
            }
        }
        this.notifySuccess("Client profile removed.");

        this.setState(prev => ({
            selected: "profile",
            changed: true,
            person: {
                ...(prev.person || {}),
                flags: ((prev.person?.flags ?? 0) & ~TABS.CLIENT)
            },
            client: {},
            modal: {
                ...prev.modal,
                confirm: { ...prev.modal.confirm, show: false }
            }
        }));
    };

    onClockClick = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                clock: {
                    show: true,
                    uuid: isGuid(e?.uuid) ? e?.uuid : null
                }
            }
        }));
    }

    onClockOk = async (e) => {
        this.onClockHideModal(e);
        await this.activityRef.current.loadData();
        if (this.state.modal.billing.show) await this.billingRef.current.loadData();
        this.notifySuccess("Time entry saved.");
    }

    onClockHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                clock: {
                    show: false
                }
            }
        }));
    }

    onExpenseClick = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                expense: {
                    show: true,
                    uuid: isGuid(e?.uuid) ? e?.uuid : null
                }
            }
        }));
    }

    onExpenseOk = async (e) => {
        this.onExpenseHideModal(e);
        await this.activityRef.current.loadData();
        if (this.state.modal.billing.show) await this.billingRef.current.loadData();
        this.notifySuccess("Expense saved.");
    }

    onExpenseHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                expense: {
                    show: false
                }
            }
        }));
    }

    onPaymentClick = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                payment: {
                    show: true,
                    uuid: isGuid(e?.uuid) ? e?.uuid : null
                }
            }
        }));
    }

    onPaymentOk = async (e) => {
        this.onPaymentHideModal(e);
        await this.activityRef.current.loadData();
        if (this.state.modal.billing.show) await this.billingRef.current.loadData();
        this.notifySuccess("Payment saved.");
    }

    onPaymentHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                payment: {
                    show: false
                }
            }
        }));
    }

    onDeletePersonClick = () => {
        if (this.isNew || this.isCompany || !isGuid(this.state.uuid)) return;

        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: {
                    show: true
                }
            }
        }));
    }

    onDeleteCompleteExtract = async () => {
        const name = sanitizeFilename(`${this.state.person?.displayName || this.state.uuid} Complete Extract.zip`);
        await this.props.completeExtract({ uuid: this.state.uuid, name });
        this.notifySuccess("Extract downloaded.");
    }

    onDeletePersonHideModal = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: {
                    show: false
                }
            }
        }));
    }

    onDeletePersonOk = async ({ selection, profileAction }) => {
        if (!isGuid(this.state.uuid)) return;

        const result = await this.props.deletePerson({ uuid: this.state.uuid, selection, profileAction });
        if (result?.success) {
            this.onDeletePersonHideModal();
            if (result.deleted) {
                this.suppressNavigationGuard = true;
                this.notifySuccess("Person deleted.");
                this.props.navigate("/dashboard/people", { replace: true });
                return;
            }

            if (result.archived) {
                this.notifySuccess("Person archived.");
            } else {
                this.notifySuccess("Selected person data deleted.");
            }

            await this.loadData();
            return;
        }

        this.onDeletePersonHideModal();
        this.onErrorShow({
            title: "Person data not deleted",
            body: result?.error?.message || "The selected person data could not be deleted."
        });
    }

    onUnarchivePersonClick = async () => {
        if (this.isNew || this.isCompany || !isGuid(this.state.uuid)) return;
        const result = await this.props.archivePerson({ uuid: this.state.uuid, archive: false });
        if (result?.success) {
            this.notifySuccess("Person unarchived.");
            await this.loadData();
            return;
        }

        this.onErrorShow({
            title: "Person not unarchived",
            body: result?.error?.message || "The person could not be unarchived."
        });
    }

    onLeadActivityClick = (item = {}) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                leadActivity: {
                    show: true,
                    uuid: isGuid(item?.uuid) ? item.uuid : null,
                    type: item?.type || "NOTE",
                    subject: item?.subject || "",
                    body: item?.body || "",
                    dueAt: item?.dueAt || null,
                    completedAt: item?.completedAt || null,
                    assignedToUuid: item?.assignedToUuid || null
                }
            }
        }));
    }

    onLeadActivityOk = async () => {
        this.onLeadActivityHideModal();
        await this.leadRef.current?.loadActivities?.();
        await this.loadData();
        this.notifySuccess("Lead activity saved.");
    }

    onLeadActivityHideModal = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                leadActivity: {
                    show: false,
                    uuid: null
                }
            }
        }));
    }

    onDeleteLeadActivityClick = (item = {}) => {
        if (!isGuid(item.uuid)) return;
        this.onConfirmShow({
            title: "Delete lead activity?",
            body: "Click ok to delete this lead activity.",
            onOk: () => this.onConfirmDeleteLeadActivity(item)
        });
    }

    onConfirmDeleteLeadActivity = async (item) => {
        const result = await this.props.deleteLeadActivity({ uuid: item.uuid });
        this.onConfirmHideModal();
        if (result?.success) {
            await this.leadRef.current?.loadActivities?.();
            await this.loadData();
            this.notifySuccess("Lead activity deleted.");
        } else {
            this.onErrorShow({
                title: "Lead activity not deleted",
                body: result?.error?.message || "The lead activity could not be deleted."
            });
        }
    }

    onBillingOk = async ({ applied, payment, remaining }) => {
        this.onBillingHideModal();
        await this.props.generateInvoice({
            personUuid: this.state.uuid,
            staffUuid: this.props.auth.user.uuid,
            applied, payment, remaining
        });

        await this.activityRef.current.loadData();
        this.notifySuccess("Invoice generated.");
    }

    onBillingHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                billing: {
                    show: false
                }
            }
        }));
    }

    onReportClick = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                report: {
                    ...prev.modal.report,
                    show: true,
                    working: false,
                    workingLabel: ""
                }
            }
        }));
    }

    closeReportModal = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                report: {
                    ...prev.modal.report,
                    show: false,
                    working: false,
                    workingLabel: ""
                }
            }
        }));
    }

    setReportWorking = (working, workingLabel = "") => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                report: {
                    ...prev.modal.report,
                    working,
                    workingLabel: working ? workingLabel : ""
                }
            }
        }));
    }

    onReportOk = async (e) => {
        let startedWorking = false;

        try {
            const selectedReport = e?.report?.target?.value;

            if (selectedReport?.blank) {
                this.closeReportModal();
                this.onEmailShow();
                return;
            }

            if (selectedReport?.style === GENERATORS.EXTRACT) {
                this.closeReportModal();
                this.onExtractShow(selectedReport);
                return;
            }

            const wantsSignatureRequest = e?.signatureRequest === true;
            const workingLabel = wantsSignatureRequest
                ? "Preparing signature request..."
                : selectedReport?.style === GENERATORS.EMAIL
                    ? "Sending email..."
                    : "Generating document...";

            this.setReportWorking(true, workingLabel);
            this.props.loading(true);
            startedWorking = true;

            const firm = await this.props.getSettings({ authentic: true, category: "Firm" });
            const firmSettings = templateFirmSettings(firm);
            const dataset = applyCustomValues({
                Firm: firmSettings,
                Person: this.state.person,
                Lead: this.state.lead,
                Client: this.state.client,
                Surveys: this.state.invites,
                ...signatureRequestTemplateData(wantsSignatureRequest),
            }, e?.custom, getTemplateCustomFields(selectedReport));

            if (selectedReport.style === 1) {
                const result = await this.props.sendEmail(
                    {
                        name: selectedReport.name,
                        json: dataset,
                        recipient: this.state.person.email
                    }
                );
                console.log(result);
                this.props.sendAlert(`${selectedReport.caption} sent!`, "success");
                this.closeReportModal();
            } else if (selectedReport.style === 2) {
                const report = selectedReport;
                const outputType = renderingStyle(report.output);

                if (!outputType) {
                    throw new Error("Unsupported report output.");
                }

                if (wantsSignatureRequest) {
                    const recipients = profileSignatureRecipients(this.state.person, firmSettings);
                    if (!recipients.some((recipient) => recipient.key === "client")) {
                        throw new Error("The profile needs an email address before a signature request can be prepared.");
                    }

                    const signatureResult = await this.props.prepareSignature({
                        json: dataset,
                        uuid: report.uuid,
                        output: report.output,
                        title: report.caption || report.name || report.fileName,
                        ownerType: "person",
                        ownerUuid: this.state.person.uuid,
                        contextType: "person",
                        contextUuid: this.state.person.uuid,
                        personUuid: this.state.person.uuid,
                        ownerPersonUuid: this.state.person.uuid,
                        recipients,
                    });
                    const envelopeUuid = signatureResult?.item?.envelope?.uuid;
                    if (!isGuid(envelopeUuid)) {
                        throw new Error("The signature request was created, but the envelope could not be opened.");
                    }

                    this.closeReportModal();
                    this.props.navigate(`/dashboard/signatures/${envelopeUuid}`);
                    return;
                }

                const reportGenerators = renderingGenerators({
                    render: this.props.render,
                });

                const generate = reportGenerators[report.output];

                if (generate) {
                    const result = await generate({
                        json: dataset,
                        uuid: report.uuid,
                        output: report.output,
                    });

                    const resolvedFileName = await renderingFileName({
                        inline: this.props.inline,
                        sanitizeFilename,
                        fileName: report.fileName,
                        json: dataset,
                        output: report.output,
                    });

                    renderedDownload({
                        result,
                        fileName: resolvedFileName,
                        mime: outputType.mime,
                    });
                    this.notifySuccess(`${selectedReport.caption} generated.`);
                    this.closeReportModal();
                } else {
                    throw new Error("No report generator is available for the selected output.");
                }
            } else {
                this.closeReportModal();
            }
        } catch (err) {
            this.onErrorShow({
                title: "Report generation failed",
                body: reportErrorMessage(err),
            });
        } finally {
            if (startedWorking) {
                this.setReportWorking(false);
                this.props.loading(false);
            }
        }
    }

    onReportHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                report: prev.modal.report?.working
                    ? prev.modal.report
                    : {
                        ...prev.modal.report,
                        show: false
                    }
            }
        }));
    }

    onExtractShow = (report) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                extract: {
                    show: true,
                    report
                }
            }
        }));
    }

    onExtractOk = async ({ selection }) => {
        const report = this.state.modal.extract.report || COMPLETE_EXTRACT_OPTION;
        this.onExtractHideModal();

        const name = sanitizeFilename(`${this.state.person?.displayName || this.state.uuid} Complete Extract.zip`);
        await this.props.completeExtract({ uuid: this.state.uuid, name, selection });
        this.notifySuccess(`${report.caption} downloaded.`);
    }

    onExtractHideModal = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                extract: {
                    show: false,
                    report: null
                }
            }
        }));
    }

    onEmailShow = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                email: {
                    ...prev.modal.email,
                    show: true
                }
            }
        }));
    }

    onEmailHideModal = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                email: {
                    ...prev.modal.email,
                    show: false
                }
            }
        }));
    }

    get emailContext() {
        return {
            ...this.state,
            surveys: this.state.invites
        };
    }

    onInviteClick = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                invite: {
                    show: true
                }
            }
        }));
    }

    onInviteOk = async (invites) => {
        for (let i = 0; i < this.state?.surveys?.length; i++) {
            const survey = this.state.surveys[i];
            if (Boolean(invites[survey.uuid])) {
                await this.props.invite({ personUuid: this.state.uuid, surveyUuid: survey.uuid });
            }
        }
        await this.loadInvites(true);
        this.onInviteHideModal();
        this.notifySuccess("Survey invitations saved.");
    }

    onInviteHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                invite: {
                    ...prev.modal.invite,
                    show: false
                }
            }
        }));
    }

    onProcessOk = async (payload) => {
        this.onProcessHideModal();

        if (!payload) return;

        const target =
            payload.mode === "update" && isGuid(payload.matterUuid)
                ? `/dashboard/matters/${payload.matterUuid}`
                : `/dashboard/matters/add`;

        this.props.navigate(target, {
            state: {
                surveyImport: payload
            }
        });
    }

    onProcessHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                process: {
                    ...prev.modal.process,
                    show: false
                }
            }
        }));
    }

    onClickCreateLead = (e) => {
        this.onConfirmShow({
            title: "Create lead profile?",
            body: "Click ok to create a lead profile for this person.",
            onOk: this.onConfirmCreateLead
        });
    }

    onClickCreateClient = (e) => {
        if (this.shouldConvertLeadToClient) {
            this.onClickConvertLead();
            return;
        }

        this.onConfirmShow({
            title: "Create client profile?",
            body: "Click ok to create a client profile for this person.",
            onOk: this.onConfirmCreateClient
        });
    }

    onClickCreateStaff = (e) => {
        this.onConfirmShow({
            title: "Create staff profile?",
            body: "Click ok to create a staff profile for this person.",
            onOk: this.onConfirmCreateStaff
        });
    }

    onClickDestroyStaff = (e) => {
        this.onConfirmShow({
            title: "Remove staff profile?",
            body: "Click ok to remove the staff profile for this person.",
            onOk: this.onConfirmDestroyStaff
        });
    }

    onConfirmDestroyStaff = async (e) => {
        if (isGuid(this.state.staff.uuid)) await this.props.deleteStaff({ uuid: this.state.staff.uuid });
        this.notifySuccess("Staff profile removed.");

        this.setState(prev => ({
            selected: "profile",
            changed: true,
            person: {
                ...prev.person,
                flags: prev.person.flags & ~TABS.STAFF
            },
            staff: {},
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onClickCreateVendor = (e) => {
        this.onConfirmShow({
            title: "Create vendor profile?",
            body: `Click ok to create a vendor profile for this ${this.isCompany ? "company" : "person"}.`,
            onOk: this.onConfirmCreateVendor
        });
    }

    onClickDestroyVendor = (e) => {
        this.onConfirmShow({
            title: "Remove vendor profile?",
            body: `Click ok to remove the vendor profile for this ${this.isCompany ? "company" : "person"}.`,
            onOk: this.onConfirmDestroyVendor
        });
    }

    onConfirmDestroyVendor = async (e) => {
        if (isGuid(this.state.vendor.uuid)) await this.props.deleteVendor({ uuid: this.state.vendor.uuid });
        this.notifySuccess("Vendor profile removed.");

        this.setState(prev => ({
            selected: "profile",
            changed: true,
            person: {
                ...prev.person,
                flags: prev.person.flags & ~TABS.VENDOR
            },
            vendor: {},
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onConfirmCreateVendor = (e) => {
        this.setState(prev => ({
            selected: "vendor",
            changed: true,
            person: {
                ...prev.person,
                flags: prev.person.flags | TABS.VENDOR
            },
            vendor: {},
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onClickCreateJudge = (e) => {
        this.onConfirmShow({
            title: "Create judge profile?",
            body: "Click ok to create a judge profile for this person. This enables the person to be found as a judge in matter management.",
            onOk: this.onConfirmCreateJudge
        });
    }

    onClickDestroyJudge = (e) => {
        this.onConfirmShow({
            title: "Remove judge profile?",
            body: "Click ok to remove the judge profile for this person. If this person has been assigned to a matter, this action will remove that association.",
            onOk: this.onConfirmDestroyJudge
        });
    }

    onConfirmDestroyJudge = async (e) => {
        if (isGuid(this.state.judge.uuid)) await this.props.deleteJudge({ uuid: this.state.judge.uuid });
        this.notifySuccess("Judge profile removed.");

        this.setState(prev => ({
            selected: "profile",
            changed: true,
            person: {
                ...prev.person,
                flags: prev.person.flags & ~TABS.JUDGE
            },
            judge: {},
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onConfirmCreateJudge = (e) => {
        this.setState(prev => ({
            selected: "judge",
            changed: true,
            person: {
                ...prev.person,
                flags: prev.person.flags | TABS.JUDGE
            },
            judge: {},
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onClickCreateAttorney = (e) => {
        this.onConfirmShow({
            title: "Create attorney profile?",
            body: "Click ok to create an attorney profile for this person.",
            onOk: this.onConfirmCreateAttorney
        });
    }

    onClickDestroyAttorney = (e) => {
        this.onConfirmShow({
            title: "Remove attorney profile?",
            body: "Click ok to remove the attorney profile for this person.",
            onOk: this.onConfirmDestroyAttorney
        });
    }

    onConfirmDestroyAttorney = async (e) => {
        if (isGuid(this.state.attorney.uuid)) await this.props.deleteAttorney({ uuid: this.state.attorney.uuid });
        this.notifySuccess("Attorney profile removed.");

        this.setState(prev => ({
            selected: "profile",
            changed: true,
            person: {
                ...prev.person,
                flags: prev.person.flags & ~TABS.ATTORNEY
            },
            attorney: {},
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onConfirmCreateAttorney = (e) => {
        this.setState(prev => ({
            selected: "attorney",
            changed: true,
            person: {
                ...prev.person,
                flags: prev.person.flags | TABS.ATTORNEY
            },
            attorney: {},
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    onLogSave = async (text) => {
        const record = {
            uuid: uuidv4(),
            target: this.state.uuid,
            category: "Note",
            authorUuid: this.props.auth.user.uuid,
            code: 0,
            meta: text
        };

        await this.props.saveLog(record);
        this.setState(prev => ({
            logs: [{
                ...record,
                Author: {
                    firstName: this.props.auth.user.firstName,
                    lastName: this.props.auth.user.lastName
                }
            }, ...(prev.logs || [])]
        }));
        this.notifySuccess("Note saved.");
    }

    onEmailSent = async (email) => {
        const sentAt = new Date();
        const author = this.props.auth?.user || {};
        const record = {
            uuid: uuidv4(),
            target: this.state.uuid,
            category: "Note",
            authorUuid: author.uuid,
            code: 0,
            meta: buildSentEmailNote(email, sentAt)
        };

        const result = await this.props.saveLog(record);
        if (!result?.success) {
            this.props.sendAlert("Email sent, but the note could not be recorded.", "error");
            return;
        }

        this.setState(prev => ({
            logs: [{
                ...record,
                ...result,
                createdAt: result.createdAt || sentAt.toISOString(),
                Author: {
                    firstName: author.firstName || "",
                    lastName: author.lastName || ""
                }
            }, ...(prev.logs || [])]
        }));
        this.notifySuccess("Email recorded in notes.");
    }

    onPendingNoteChange = (pendingNote) => {
        this.setState({
            pendingNote
        });
    }

    savePendingNote = async (target = this.state.uuid) => {
        if (!this.notesRef.current?.hasPendingText?.()) return;

        const record = {
            uuid: uuidv4(),
            target,
            category: "Note",
            authorUuid: this.props.auth.user.uuid,
            code: 0,
            meta: this.notesRef.current.getPendingText()
        };

        await this.props.saveLog(record);
        this.notesRef.current.clearPendingText();
        this.setState({
            pendingNote: false
        });
        this.notifySuccess("Note saved.");
    }

    onInvoiceClick = async () => {
        await this.billingRef.current.loadData();
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                billing: {
                    show: true
                }
            }
        }));
    }

    onInvoiceOpen = async ({ uuid }) => {
        const dataset = await this.props.getInvoice({ uuid });
        const firm = await this.props.getSettings({ authentic: true, category: "Firm" });
        dataset.Firm = templateFirmSettings(firm);

        const reports = await this.props.getReport(null, "INVOICE");
        const report = reports?.[0];
        if (!report) {
            this.props.sendAlert("Invoice not found!", "error");
            return;
        }
    
        const fileName = sanitizeFilename(await this.props.inline({ text: report.fileName, json: dataset }));
        const rendered = await this.props.render({ json: dataset, uuid: report.uuid, output: report.output });
        const isDocx = report.output === "RTF";
        const mime = isDocx
            ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            : "application/pdf";
        const blob = rendered instanceof Blob ? rendered : new Blob([rendered], { type: mime });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 2000);
    }

    onActivityItemClick = (data) => {
        if (isGuid(data.invoiceUuid)) return;

        switch (data.type) {
            case "INVOICE":
                this.onInvoiceOpen({ uuid: data.uuid });
                break;
            case "CLOCK":
                this.onClockClick({ uuid: data.uuid });
                break;
            case "PAYMENT":
                this.onPaymentClick({ uuid: data.uuid });
                break;
            case "EXPENSE":
                this.onExpenseClick({ uuid: data.uuid });
                break;
            default:
                break;
        }
    }

    onConfirmDeleteActivity = async (data) => {
        await this.props.deleteActivity(data);
        await this.activityRef.current.loadData();
        this.onConfirmHideModal();
        this.notifySuccess("Activity deleted.");
    }

    onActivityDeleteClick = (data) => {
        switch (data.type) {
            case "INVOICE":
                this.onConfirmShow({
                    title: "Delete Invoice?",
                    body: `Click ok to delete invoice #${data.number}.`,
                    onOk: (e) => this.onConfirmDeleteActivity({ uuid: data.uuid })
                });
                break;
            case "CLOCK":
                this.onConfirmShow({
                    title: "Delete Time?",
                    body: `Click ok to delete the time spent by ${data.staffDisplayName} ($${Math.abs(data.signed)}).`,
                    onOk: (e) => this.onConfirmDeleteActivity({ activityUuid: data.uuid })
                });
                break;
            case "EXPENSE":
                this.onConfirmShow({
                    title: "Delete expense?",
                    body: `Click ok to delete the expense of $${Math.abs(data.signed)} by ${data.staffDisplayName}.`,
                    onOk: (e) => this.onConfirmDeleteActivity({ activityUuid: data.uuid })
                });
                break;
            case "PAYMENT":
                this.onConfirmShow({
                    title: "Delete payment?",
                    body: `Click ok to delete the payment of $${Math.abs(data.signed)} by ${data.personDisplayName}.`,
                    onOk: (e) => this.onConfirmDeleteActivity({ activityUuid: data.uuid })
                });
                break;
            default:
                break;
        }
    }

    onMatterClick = () => {
        const person = this.state.person || {};
        const displayName = person.displayName || [person.firstName, person.lastName].filter(Boolean).join(" ").trim();

        this.props.navigate("/dashboard/matters/add", {
            state: {
                clientSeed: {
                    person: {
                        uuid: this.state.uuid,
                        displayName,
                        firstName: person.firstName,
                        lastName: person.lastName,
                        email: person.email,
                        phone: person.phone,
                        flags: person.flags,
                    },
                    defaultCaption: `Matter for ${displayName || "Client"}`,
                    role: "Plaintiff",
                },
            },
        });
    };

    get RecordActions() {
        const result = [];

        if (!this.isNew && this.has({ flag: TABS.CLIENT }) && this.permitted(EDIT_MATTER)) {
            result.push({
                caption: "Matter",
                onClick: this.onMatterClick,
            });
        }

        if (!this.isNew && this.has({ flag: TABS.LEAD }) && this.permitted(EDIT_LEAD)) {
            result.push({
                caption: "Lead Activity",
                onClick: this.onLeadActivityClick,
            });
        }

        if (this.permitted(EDIT_EXPENSE)) {
            result.push({
                caption: "Expense",
                onClick: this.onExpenseClick,
            });
        }

        if (this.permitted(EDIT_PAYMENT)) {
            result.push({
                caption: "Payment",
                onClick: this.onPaymentClick,
            });
        }

        if (this.permitted(EDIT_CLOCK)) {
            result.push({
                caption: "Time",
                onClick: this.onClockClick,
            });
        }

        if (this.permitted(EDIT_INVOICE)) {
            result.push({
                caption: "Invoice",
                onClick: this.onInvoiceClick
            });
        }

        return result;
    }

    get ProfileAddActions() {
        const result = [];

        if (this.isNew) return result;

        if (this.permitted(EDIT_CLIENT) && !this.has({ flag: TABS.CLIENT })) {
            result.push({
                caption: "Client Profile",
                onClick: this.onClickCreateClient
            });
        }

        if (this.permitted(EDIT_LEAD) && hasFeature(FEATURES.LEADS) && !this.has({ flag: TABS.LEAD })) {
            result.push({
                caption: "Lead Profile",
                onClick: this.onClickCreateLead
            });
        }

        if (!this.isCompany && this.permitted(EDIT_ATTORNEY) && !this.has({ flag: TABS.ATTORNEY })) {
            result.push({
                caption: "Attorney Profile",
                onClick: this.onClickCreateAttorney
            });
        }

        if (!this.isCompany && this.permitted(EDIT_STAFF) && !this.has({ flag: TABS.STAFF })) {
            result.push({
                caption: "Staff Profile",
                onClick: this.onClickCreateStaff
            });
        }

        if (!this.isCompany && this.permitted(EDIT_JUDGE) && !this.has({ flag: TABS.JUDGE })) {
            result.push({
                caption: "Judge Profile",
                onClick: this.onClickCreateJudge
            });
        }

        if (this.permitted(EDIT_VENDOR) && !this.has({ flag: TABS.VENDOR })) {
            result.push({
                caption: "Vendor Profile",
                onClick: this.onClickCreateVendor
            });
        }

        return result;
    }

    get AddActions() {
        const profileActions = this.ProfileAddActions;
        const recordActions = this.RecordActions;

        if (profileActions.length > 0 && recordActions.length > 0) {
            return [...profileActions, { divider: true }, ...recordActions];
        }

        return [...profileActions, ...recordActions];
    }

    get displayAdd() {
        return this.AddActions.length > 0;
    }

    render() {
        const hasSavedPerson = !this.isNew && isGuid(this.state.uuid);

        return (
            <>
                <NavigationGuard
                    when={() => !this.suppressNavigationGuard && this.hasUnsavedChanges}
                    message="This person has unsaved changes. Leave without saving?"
                />
                <Col md="8" className="float-right text-end">
                    <ConditionalDropdown enabledVariant="outline-primary" display={this.displayAdd} enabled icon="clock" items={this.AddActions}>Add</ConditionalDropdown>
                    <ConditionalButton enabledVariant="outline-primary" tooltip="Invite to Survey" display={(this.permitted(EDIT_CLIENT) || this.permitted(EDIT_LEAD)) && hasFeature(FEATURES.INTAKES)} enabled={hasFeature(FEATURES.INTAKES)} onEnabledClick={this.onInviteClick} icon="email">Survey</ConditionalButton>
                    <ConditionalButton enabledVariant="outline-primary" tooltip="Generate a Document" display={!this.isNew} enabled={!Boolean(this.state.changed)} onEnabledClick={this.onReportClick} icon="content">Generate</ConditionalButton>
                    <ConditionalButton enabledVariant="outline-danger" tooltip="Delete person" display={!this.isNew && !this.isCompany} enabled={!Boolean(this.state.changed)} onEnabledClick={this.onDeletePersonClick} icon="bin">Delete</ConditionalButton>
                    <ConditionalButton enabledVariant="outline-primary" tooltip={`Save ${this.entityName}`} display={true} enabled={true} onEnabledClick={this.onSaveClick} icon="save">Save</ConditionalButton>
                </Col>
                <Tab.Container activeKey={this.state.selected}>
                    <Card.Header className="app-tab-header border-0 pb-0">
                        <Nav className="app-tab-nav nav-tabs-line card-header-tabs" variant="tabs" activeKey={this.state.selected}>
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    href="#profile"
                                    className={`${this.state.selected === "profile" ? "active" : ""} app-tab-link`}
                                    onClick={(event) => {
                                        this.onTabChange(event, "profile");
                                    }}
                                >
                                    Profile
                                </Nav.Link>
                            </Nav.Item>

                            {this.isCompany && !this.isNew ? (
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    href="#people"
                                    className={`${this.state.selected === "people" ? "active" : ""} app-tab-link`}
                                    onClick={(event) => {
                                        this.onTabChange(event, "people");
                                    }}
                                >
                                    People
                                </Nav.Link>
                            </Nav.Item>
                            ) : null}

                            <NavItem
                                name="lead"
                                caption="Lead"
                                selected={this.state.selected}
                                hidden={!this.permitted(EDIT_LEAD) || !hasFeature(FEATURES.LEADS) || !this.has({ flag: TABS.LEAD })}
                                creator={false}
                                created={this.has({ flag: TABS.LEAD })}
                                destroyer={this.permitted(DELETE_LEAD)}
                                onClick={(e) => this.onTabChange(e, "lead")}
                                onDestroy={this.onClickDestroyLead}
                            />

                            <NavItem
                                name="client"
                                caption="Client"
                                selected={this.state.selected}
                                hidden={!this.permitted(EDIT_CLIENT) || !this.has({ flag: TABS.CLIENT })}
                                creator={false}
                                created={this.has({ flag: TABS.CLIENT })}
                                destroyer={this.permitted(DELETE_CLIENT)}
                                onClick={(e) => this.onTabChange(e, "client")}
                                onDestroy={this.onClickDestroyClient}
                            />

                            <NavItem
                                name="attorney"
                                caption="Attorney"
                                selected={this.state.selected}
                                hidden={this.isCompany || !this.permitted(EDIT_ATTORNEY) || !this.has({ flag: TABS.ATTORNEY })}
                                creator={false}
                                created={this.has({ flag: TABS.ATTORNEY })}
                                destroyer={this.permitted(DELETE_ATTORNEY)}
                                onClick={(e) => this.onTabChange(e, "attorney")}
                                onDestroy={this.onClickDestroyAttorney}
                            />

                            <NavItem
                                name="judge"
                                caption="Judge"
                                selected={this.state.selected}
                                hidden={this.isCompany || !this.permitted(EDIT_JUDGE) || !this.has({ flag: TABS.JUDGE })}
                                creator={false}
                                created={this.has({ flag: TABS.JUDGE })}
                                destroyer={this.permitted(DELETE_JUDGE)}
                                onClick={(e) => this.onTabChange(e, "judge")}
                                onDestroy={this.onClickDestroyJudge}
                            />

                            <NavItem
                                name="staff"
                                caption="Staff"
                                selected={this.state.selected}
                                hidden={this.isCompany || !this.permitted(EDIT_STAFF) || !this.has({ flag: TABS.STAFF })}
                                creator={false}
                                created={this.has({ flag: TABS.STAFF })}
                                destroyer={this.permitted(DELETE_STAFF)}
                                onClick={(e) => this.onTabChange(e, "staff")}
                                onDestroy={this.onClickDestroyStaff}
                            />

                            <NavItem
                                name="vendor"
                                caption="Vendor"
                                selected={this.state.selected}
                                hidden={!this.permitted(EDIT_VENDOR) || !this.has({ flag: TABS.VENDOR })}
                                creator={false}
                                created={this.has({ flag: TABS.VENDOR })}
                                destroyer={this.permitted(DELETE_VENDOR)}
                                onClick={(e) => this.onTabChange(e, "vendor")}
                                onDestroy={this.onClickDestroyVendor}
                            />

                            {!this.isNew ? (
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    href="#board"
                                    className={`${this.state.selected === "board" ? "active" : ""} app-tab-link`}
                                    onClick={(event) => {
                                        this.onTabChange(event, "board");
                                    }}
                                >
                                    Board
                                </Nav.Link>
                            </Nav.Item>
                            ) : null}

                            {!this.isNew ? (
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    href="#calendar"
                                    className={`${this.state.selected === "calendar" ? "active" : ""} app-tab-link`}
                                    onClick={(event) => {
                                        this.onTabChange(event, "calendar");
                                    }}
                                >
                                    Calendar
                                </Nav.Link>
                            </Nav.Item>
                            ) : null}

                            {!this.isNew ? (
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    href="#files"
                                    className={`${this.state.selected === "files" ? "active" : ""} app-tab-link`}
                                    onClick={(event) => {
                                        this.onTabChange(event, "files");
                                    }}
                                >
                                    Files
                                </Nav.Link>
                            </Nav.Item>
                            ) : null}

                            {!this.isNew ? (
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    href="#notes"
                                    className={`${this.state.selected === "notes" ? "active" : ""} app-tab-link`}
                                    onClick={(event) => {
                                        this.onTabChange(event, "notes");
                                    }}
                                >
                                    Notes
                                </Nav.Link>
                            </Nav.Item>
                            ) : null}
                        </Nav>
                    </Card.Header>
                    <Col md="12">
                        <Form name="frm" action="post" onSubmit={this.onSubmit}>
                            <Row>
                                <Col>
                                    <Tab.Content className="app-tab-content-min h-100">
                                        <Tab.Pane active={this.state.selected === "profile"} className={`h-100 scroll-out ${this.state.selected === "profile" ? "d-block" : "d-none"}`}>
                                            <Card body className="mb-3">
                                                <Personal {...this.state.person} company={this.isCompany} onErrorShow={this.onErrorShow} onChange={this.onPersonChange} onUnarchive={this.onUnarchivePersonClick} />
                                            </Card>
                                        </Tab.Pane>

                                        {this.isCompany && !this.isNew ? (
                                        <Tab.Pane active={this.state.selected === "people"} className={`h-100 scroll-out ${this.state.selected === "people" ? "d-block" : "d-none"}`}>
                                            {this.state.selected === "people" ? (
                                                <Card body className="mb-5">
                                                    <CompanyPeople
                                                        ref={this.companyPeopleRef}
                                                        companyUuid={this.state.uuid}
                                                    />
                                                </Card>
                                            ) : null}
                                        </Tab.Pane>
                                        ) : null}

                                        <Tab.Pane active={this.state.selected === "lead"} className={`h-100 scroll-out ${this.state.selected === "lead" ? "d-block" : "d-none"}`} hidden={!this.permitted(EDIT_LEAD)}>
                                            {this.state.selected === "lead" && this.has({ flag: TABS.LEAD }) ? (
                                                <Lead
                                                    ref={this.leadRef}
                                                    {...this.state.lead}
                                                    getStaff={this.props.getStaff}
                                                    getLeadActivities={this.props.getLeadActivities}
                                                    completeLeadActivity={this.props.completeLeadActivity}
                                                    onEditLeadActivity={this.onLeadActivityClick}
                                                    onDeleteLeadActivity={this.onDeleteLeadActivityClick}
                                                    onErrorShow={this.onErrorShow}
                                                />
                                            ) : null}
                                        </Tab.Pane>

                                        <Tab.Pane active={this.state.selected === "client"} className={`h-100 scroll-out ${this.state.selected === "client" ? "d-block" : "d-none"}`} hidden={!this.permitted(EDIT_CLIENT)}>
                                            {this.state.selected === "client" && this.has({ flag: TABS.CLIENT }) ? (
                                                <Card body className="mb-5">
                                                    <Client
                                                        ref={this.clientRef}
                                                        {...this.state.client}
                                                        onErrorShow={this.onErrorShow}
                                                    />
                                                </Card>
                                            ) : null}
                                        </Tab.Pane>

                                        <Tab.Pane active={this.state.selected === "attorney"} className={`h-100 scroll-out ${this.state.selected === "attorney" ? "d-block" : "d-none"}`} hidden={this.isCompany || !this.permitted(EDIT_ATTORNEY)}>
                                            {this.state.selected === "attorney" && this.has({ flag: TABS.ATTORNEY }) ? (
                                                <Card body className="mb-5">
                                                    <Attorney
                                                        ref={this.attorneyRef}
                                                        {...this.state.attorney}
                                                        onErrorShow={this.onErrorShow}
                                                    />
                                                </Card>
                                            ) : null}
                                        </Tab.Pane>

                                        <Tab.Pane active={this.state.selected === "judge"} className={`h-100 scroll-out ${this.state.selected === "judge" ? "d-block" : "d-none"}`} hidden={this.isCompany || !this.permitted(EDIT_JUDGE)}>
                                            {this.state.selected === "judge" && this.has({ flag: TABS.JUDGE }) ? (
                                                <Card body className="mb-5">
                                                    <Judge
                                                        ref={this.judgeRef}
                                                        {...this.state.judge}
                                                        onErrorShow={this.onErrorShow}
                                                    />
                                                </Card>
                                            ) : null}
                                        </Tab.Pane>

                                        <Tab.Pane active={this.state.selected === "staff"} className={`h-100 scroll-out ${this.state.selected === "staff" ? "d-block" : "d-none"}`} hidden={this.isCompany}>
                                            {this.state.selected === "staff" && this.has({ flag: TABS.STAFF }) ? (
                                                <Card body className="mb-5">
                                                    <Staff
                                                        ref={this.staffRef}
                                                        {...this.state.staff}
                                                        onErrorShow={this.onErrorShow}
                                                    />
                                                </Card>
                                            ) : null}
                                        </Tab.Pane>

                                        <Tab.Pane active={this.state.selected === "vendor"} className={`h-100 scroll-out ${this.state.selected === "vendor" ? "d-block" : "d-none"}`} hidden={!this.permitted(EDIT_VENDOR)}>
                                            {this.state.selected === "vendor" && this.has({ flag: TABS.VENDOR }) ? (
                                                <Card body className="mb-5">
                                                    <Vendor
                                                        ref={this.vendorRef}
                                                        {...this.state.vendor}
                                                        onErrorShow={this.onErrorShow}
                                                    />
                                                </Card>
                                            ) : null}
                                        </Tab.Pane>

                                        {!this.isNew ? (
                                        <Tab.Pane active={this.state.selected === "board"} className="h-100 scroll-out">
                                            {this.state.selected === "board" ? (
                                                <Board
                                                    ref={this.boardRef}
                                                    personUuid={this.state.uuid}
                                                    personType={this.boardPersonType}
                                                    initialTaskUuid={this.openBoardTaskUuid}
                                                />
                                            ) : null}
                                        </Tab.Pane>
                                        ) : null}

                                        {!this.isNew ? (
                                        <Tab.Pane active={this.state.selected === "calendar"} className="h-100 scroll-out">
                                            {this.state.selected === "calendar" ? (
                                                <Card body className="mb-5">
                                                    <Calendar
                                                        owner={this.state.uuid}
                                                        showAvailabilityControls={false}
                                                        showHistoryView
                                                        showContextScopeToggle
                                                        contextScopeLabel={this.calendarScopeLabel}
                                                    />
                                                </Card>
                                            ) : null}
                                        </Tab.Pane>
                                        ) : null}

                                        {!this.isNew ? (
                                        <Tab.Pane active={this.state.selected === "files"} className="h-100 scroll-out">
                                            <Card body className="mt-5">
                                                <Files
                                                    ownerType="person"
                                                    ownerUuid={this.state.uuid}
                                                />
                                            </Card>
                                        </Tab.Pane>
                                        ) : null}

                                        {!this.isNew ? (
                                        <Tab.Pane active={this.state.selected === "notes"} className="h-100 scroll-out">
                                            <Notes ref={this.notesRef} logs={this.state.logs} onSave={this.onLogSave} onPendingChange={this.onPendingNoteChange} />
                                        </Tab.Pane>
                                        ) : null}
                                    </Tab.Content>
                                </Col>
                            </Row>
                            {hasSavedPerson ? (
                            <Row hidden={this.state.selected === "notes" || this.state.selected === "calendar" || this.state.selected === "files" || this.state.selected === "board" || this.state.selected === "people"}>
                                <Col>
                                    <Matters person={this.state.uuid} ref={this.mattersRef}></Matters>
                                    <SignatureRequests person={this.state.uuid} />
                                    <Card body className="mb-5">
                                        <Activity person={this.state.uuid} onDeleteClick={this.onActivityDeleteClick} onItemClick={this.onActivityItemClick} ref={this.activityRef}></Activity>
                                    </Card>
                                    {this.has({ flag: TABS.CLIENT }) ? (
                                        <Card body className="mb-5">
                                            <TrustActivity clientUuid={this.state.uuid} updated={this.state.client} />
                                        </Card>
                                    ) : null}
                                </Col>
                            </Row>
                            ) : null}
                        </Form>
                    </Col>
                </Tab.Container>

                <ErrorModal {...this.state.modal.error} buttons={
                    [
                        {
                            default: true,
                            caption: "ok",
                            onEnabledClick: this.onErrorHideModal,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        }
                    ]
                }></ErrorModal>

                <ConfirmModal {...this.state.modal.confirm} buttons={
                    [
                        ...(this.state.modal.confirm.extraButtons || []),
                        {
                            caption: this.state.modal.confirm.okCaption || "ok",
                            onEnabledClick: this.state.modal.confirm.onOk,
                            variant: this.state.modal.confirm.okVariant || "secondary",
                            display: true,
                            enabled: true
                        },
                        {
                            default: true,
                            caption: "cancel",
                            onEnabledClick: this.onConfirmHideModal,
                            variant: "primary",
                            display: true,
                            enabled: true
                        }
                    ]
                }></ConfirmModal>

                <InviteModal
                    show={this.state.modal.invite.show}
                    onOk={this.onInviteOk}
                    onCancel={this.onInviteHideModal}
                    surveys={this.state.surveys}
                    invites={this.state.invites}
                    onReview={this.onSurveyReview}
                    onRemind={this.onSurveyRemind}
                    onDelete={this.onSurveyDelete}
                ></InviteModal>

                <ReportModal
                    show={this.state.modal.report.show}
                    items={this.state.reports}
                    onOk={this.onReportOk}
                    onCancel={this.onReportHideModal}
                    working={this.state.modal.report.working}
                    workingLabel={this.state.modal.report.workingLabel}
                ></ReportModal>

                <ExtractModal
                    show={this.state.modal.extract.show}
                    person={this.state.person}
                    onOk={this.onExtractOk}
                    onCancel={this.onExtractHideModal}
                ></ExtractModal>

                <DeletePersonModal
                    show={this.state.modal.delete.show}
                    person={this.state.person}
                    onOk={this.onDeletePersonOk}
                    onCancel={this.onDeletePersonHideModal}
                    onExtract={this.onDeleteCompleteExtract}
                ></DeletePersonModal>

                <EmailComposeModal
                    show={this.state.modal.email.show}
                    person={this.state.person}
                    context={this.emailContext}
                    onSend={this.onEmailSent}
                    onCancel={this.onEmailHideModal}
                /> 
                
                <ProcessModal
                    {...this.state.modal.process}
                    onOk={this.onProcessOk}
                    onCancel={this.onProcessHideModal}
                    instructions={
                        {
                            domain: "DIVORCE",
                            version: 0.1,
                            tasks: [
                                {
                                    id: "1.0",
                                    description: "Check to see if a divorce matter is available.",
                                    automatic: false,
                                    active: true,
                                    model: ""
                                }
                            ]
                        }
                    }
                ></ProcessModal>

                <ClockModal
                    show={this.state.modal.clock.show}
                    personUuid={this.state.uuid}
                    uuid={this.state.modal.clock.uuid}
                    onOk={this.onClockOk}
                    onCancel={this.onClockHideModal}
                ></ClockModal>

                <ExpenseModal
                    show={this.state.modal.expense.show}
                    personUuid={this.state.uuid}
                    uuid={this.state.modal.expense.uuid}
                    onOk={this.onExpenseOk}
                    onCancel={this.onExpenseHideModal}
                ></ExpenseModal>

                <PaymentModal
                    show={this.state.modal.payment.show}
                    uuid={this.state.modal.payment.uuid}
                    personUuid={this.state.uuid}
                    onOk={this.onPaymentOk}
                    onCancel={this.onPaymentHideModal}
                ></PaymentModal>

                <LeadActivityModal
                    {...this.state.modal.leadActivity}
                    leadUuid={this.state.uuid}
                    getStaff={this.props.getStaff}
                    saveLeadActivity={this.props.saveLeadActivity}
                    onOk={this.onLeadActivityOk}
                    onCancel={this.onLeadActivityHideModal}
                ></LeadActivityModal>

                <LeadConversionModal
                    show={this.state.modal.leadConversion.show}
                    defaultCaption={this.leadConversionMatterCaption}
                    onOk={this.onConfirmConvertLead}
                    onCancel={this.onLeadConversionHideModal}
                ></LeadConversionModal>

                <BillingModal
                    show={this.state.modal.billing.show}
                    personUuid={this.state.uuid}
                    onOk={this.onBillingOk}
                    onCancel={this.onBillingHideModal}
                    onItemClick={this.onActivityItemClick}
                    ref={this.billingRef}
                ></BillingModal>

                <ScrollToTopButton />
            </>
        );
    }
}

Detail.propTypes = {
    auth: PropTypes.object.isRequired
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    });
};

export default withNavigation(
    connect(
        mapStateToProps,
        {
            pageInfo,
            loading,
            getPerson,
            savePerson,
            deletePerson,
            archivePerson,
            completeExtract,
            getCompany,
            saveCompany,
            getAttorney,
            saveAttorney,
            deleteAttorney,
            getJudge,
            saveJudge,
            deleteJudge,
            getLead,
            saveLead,
            deleteLead,
            convertLead,
            getLeadActivities,
            saveLeadActivity,
            completeLeadActivity,
            deleteLeadActivity,
            getClient,
            saveClient,
            deleteClient,
            getStaff,
            saveStaff,
            deleteStaff,
            getVendor,
            saveVendor,
            deleteVendor,
            getRates,
            getLogs,
            saveLog,
            getSurveys,
            getInvites,
            getReports,
            getReport,
            getSettings,
            getClocks,
            render,
            inline,
            prepareSignature,
            invite,
            uninvite,
            remind,
            generateInvoice,
            deleteActivity,
            getInvoice,
            getMatters,
            getEmails,
            sendEmail, 
            sendAlert
        })(Detail)
);
